<template>
  <div class="register">
    <div :class="`step_head ${register_step == 2 ? 'step_head_2' : ''}`">
      <div v-for="i in 3" :key="i" :class="`step_head_item ${i <= register_step ? 'active' : ''}`"></div>
    </div>

    <!-- Step 1 -->
    <div :class="`step step_1 ${register_step == 1 ? 'active' : ''}`">
      <div class="title">{{ text('auth.register.title') }}</div>
      <div class="inputs">
        <Input
          v-model:value="user.first_name"
          type="text"
          :placeholder="text('auth.register.first_name')"
          :error-message="(valid.active && valid.first_name_error) ? text(`validation_fields.${valid.first_name_error}`) : ''"
        />
        <Input
          v-model:value="user.last_name"
          type="text"
          :placeholder="text('auth.register.last_name')"
          :error-message="(valid.active && valid.last_name_error) ? text(`validation_fields.${valid.last_name_error}`) : ''"
        />
        <Input
          type="email"
          v-model:value="user.email"
          :placeholder="text('auth.register.email')"
          :error-message="(valid.active && valid.email_error) ? text(`validation_fields.${valid.email_error}`) : ''"
        />

        <InputMask
          v-model:phone="user.phone"
          :error-message="(valid.active && valid.phone_error) ? text(`validation_fields.${valid.phone_error}`) : ''"
        />

        <template v-if="envName === 'grok'">
          <div class="section-divider">
            <h3>{{ text('auth.register.address_section') }}</h3>
          </div>

          <SelectCountry
            v-model="user.country_code"
            :error-message="(valid.active && valid.country_code_error) ? text(`validation_fields.${valid.country_code_error}`) : ''"
          />
          <div class="input-row">
            <Input
              type="text"
              v-model:value="user.city"
              :placeholder="text('auth.register.city')"
              :error-message="(valid.active && valid.city_error) ? text(`validation_fields.${valid.city_error}`) : ''"
            />
            <Input
              type="text"
              v-model:value="user.zip_code"
              :placeholder="text('auth.register.zip_code')"
              :error-message="(valid.active && valid.zip_code_error) ? text(`validation_fields.${valid.zip_code_error}`) : ''"
            />
          </div>
          <Input
              type="text"
              v-model:value="user.address"
              :placeholder="text('auth.register.address')"
              :error-message="(valid.active && valid.address_error) ? text(`validation_fields.${valid.address_error}`) : ''"
          />
        </template>
      </div>

      <YandexCaptcha
        ref="yandexCaptchaRef"
        @token-received="onCaptchaTokenReceived"
        @token-expired="onCaptchaTokenExpired"
      />

      <BaseButton @click="register">
        <template #label>
          {{ text('auth.register.continue_btn') }}
        </template>
      </BaseButton>

      <div class="error">{{ errorMessage }}</div>

      <div class="text">
        <router-link :to="{name: 'LogIn'}">
          {{ text('auth.register.enter_help') }}
        </router-link>
      </div>
      <br/>
      <br/>

      <StaterAccept
        v-if="envName === 'stater'"
        v-model:ofertaAccepted="ofertaAccepted"
        v-model:persDataAccepted="persDataAccepted"
      />
    </div>
    <!-- End step 1 -->

    <!-- Step 2 -->
    <div :class="`step step_2 ${register_step == 2 ? 'active' : ''}`">
      <AuthIdentification
        v-model:confirmCode="confirmCode"
        :phone="user.phone"
        @onPrevPage="changeToPrevPage"
        :title="text('auth.register.enter_code_help')"
        :description="text('auth.register.enter_code_descr')"
      />
      <div class="error">{{ errorMessage }}</div>

      <div style="margin: 0 24px;" class="text" v-if="envName === 'stater'">
        Не приходит код? <br/>
        Обратитесь в службу поддержки: <br/>
        <br/>
        <b>+7 (940) 999-11-33</b>
      </div>

      <BaseButton
        @click="phoneConfirm"
        :disabled="confirmCode.length < 4"
        style="margin: 24px 0"
      >
        <template #label>
          {{ text('auth.register.continue_btn') }}
        </template>
      </BaseButton>
      <send-again-button
        @click="sendCode()"
        :title="text('auth.register.send_again_btn')"
      />

    </div>
    <!-- End step 2 -->

    <!-- Step 3 -->
    <div :class="`step step_3 ${register_step == 3 ? 'active' : ''}`">
      <AuthIdentification
        v-model:confirmCode="pinCode"
        :prevButton="false"
        masked
        :title="text('auth.register.set_pin.help')"
        :description="text('auth.register.set_pin.descr')"
      />
      <BaseButton
        @click="savePinCode"
        :disabled="pinCode.length < 4"
        style="margin: 24px 0"
      >
        <template #label>
          {{ text('auth.register.set_pin.continue') }}
        </template>
      </BaseButton>
    </div>
    <!-- End step 3 -->

    <GrokPolicy v-if="envName === 'grok'"/>

  </div>
</template>

<script>

import Input from "@/components/Input.vue"
import InputMask from "@/components/phone_input/InputMask.vue"
import BaseButton from '@/components/base/buttons/BaseButton.vue'
import AuthIdentification from '@/components/AuthIdentification.vue'
import SendAgainButton from '@/components/SendAgainButton.vue'
import {doRegister, doRegisterPhoneConfirm, doRegisterSetPin} from "@/services/api"
import StaterAccept from '@/components/StaterAccept.vue'
import GrokPolicy from '@/components/GrokPolicy.vue'
import SelectCountry from '@/components/SelectCountry.vue'
import YandexCaptcha from '@/components/YandexCaptcha.vue'
import { isEmailValid } from '@/utils/validation.js'

export default {
  name: "Register",
  components: {
    Input,
    InputMask,
    BaseButton,
    AuthIdentification,
    SendAgainButton,
    StaterAccept,
    GrokPolicy,
    SelectCountry,
    YandexCaptcha
  },
  data() {
    return {
      register_step: 1,
      user: {
        first_name: '',
        last_name: '',
        email: '',
        address: '',
        city: '',
        country_code: '',
        zip_code: '',
        phone: ''
      },
      confirmCode: '',
      pinCode: '',
      errorMessage: '',
      ofertaAccepted: true,
      persDataAccepted: true,
      captchaToken: '',
      isValidationActive: false
    }
  },
  watch: {
    user: {
      handler() {
        this.isValidationActive = false
      },
      deep: true
    }
  },
  computed: {
    authToken() {
      return this.$store.state.authToken
    },
    isPhoneValid() {
      return this.user.phone.slice(1).length >= 8
    },
    valid() {
      const { first_name, last_name, email, country_code, city, zip_code, address } = this.user
      const { isValidationActive, isEmailValid, isPhoneValid } = this

      const isEmpty =str=> !str || str.trim() === ''
      const lettersOnly = /^[a-zA-Zа-яА-ЯёЁ\s]+$/
      const digitsOnly = /^\d+$/

      if (this.envName === 'grok') {
        return {
          active: isValidationActive,
          first_name_error: isEmpty(first_name) ? 'required' : (!lettersOnly.test(first_name.trim()) ? 'letters_only' : ''),
          last_name_error: isEmpty(last_name) ? 'required' : (!lettersOnly.test(last_name.trim()) ? 'letters_only' : ''),
          email_error:  isEmpty(email) ? 'required' : (!isEmailValid(email) ? 'email' : ''),
          phone_error: !isPhoneValid ? 'min_8' : '',
          country_code_error: isEmpty(country_code) ? 'select_country' : '',
          city_error: isEmpty(city) ? 'required' : '',
          zip_code_error: isEmpty(zip_code) ? 'required' : (!digitsOnly.test(zip_code.trim()) ? 'digits_only' : ''),
          address_error: isEmpty(address) ? 'required' : '',
          isValid: !isEmpty(first_name) && lettersOnly.test(first_name.trim()) && !isEmpty(last_name) && lettersOnly.test(last_name.trim()) && isEmailValid(email) && isPhoneValid && !isEmpty(country_code) && !isEmpty(city) && !isEmpty(zip_code) && digitsOnly.test(zip_code.trim()) && !isEmpty(address)
        }
      } else {
        return {
          active: isValidationActive,
          first_name_error: isEmpty(first_name) ? 'required' : (!lettersOnly.test(first_name.trim()) ? 'letters_only' : ''),
          last_name_error: isEmpty(last_name) ? 'required' : (!lettersOnly.test(last_name.trim()) ? 'letters_only' : ''),
          email_error:  isEmpty(email) ? 'required' : (!isEmailValid(email) ? 'email' : ''),
          phone_error: !isPhoneValid ? 'min_8' : '',
          isValid: !isEmpty(first_name) && lettersOnly.test(first_name.trim()) && !isEmpty(last_name) && lettersOnly.test(last_name.trim()) && isEmailValid(email) && isPhoneValid
        }
      }
    }
  },
  methods: {
    isEmailValid,
    async sendCode() {
      const requestData = {"phone": this.user.phone}

      // Добавляем токен капчи если он доступен
      if (this.yandexCaptchaEnabled && this.captchaToken) {
        requestData.captcha_token = this.captchaToken
      }

      const {data} = await this.$http.post(
        '/v2/wallet/auth/send_code',
        requestData,
        {
          validateStatus: (status) => (status >= 200 && status < 500)
        }
      )
    },
    async register() {
      const { isValid } = this.valid

      if (isValid) {
        this.pinCode = ''

        let resp
        let registerData

        if (this.envName === 'grok') {
          registerData = {
            "first_name": this.user.first_name,
            "last_name": this.user.last_name,
            "phone": this.user.phone,
            "email": this.user.email,
            "country_code": this.user.country_code,
            "city": this.user.city,
            "zip_code": this.user.zip_code,
            "address": this.user.address
          }
        } else {
          registerData = {
            "first_name": this.user.first_name,
            "last_name": this.user.last_name,
            "phone": this.user.phone,
            "email": this.user.email
          }
        }
        registerData.space_iname = this.envName

        // Добавляем токен капчи если он доступен
        if (this.yandexCaptchaEnabled && this.captchaToken) {
          registerData.captcha_token = this.captchaToken
        }

        resp = await doRegister(registerData)
        if (resp.status === "error") {
          this.errorMessage = resp.message
          // Сбрасываем состояние капчи при ошибке
          if (this.yandexCaptchaEnabled && this.$refs.yandexCaptchaRef) {
            this.$refs.yandexCaptchaRef.reset()
            this.captchaToken = ''
          }
        } else {
          this.errorMessage = ''
          this.confirmCode = ''
          this.register_step = 2
        }
      } else {
        this.isValidationActive = true
      }
    },
    async savePinCode() {
      await doRegisterSetPin(this.authToken, this.pinCode)
      this.$router.push('/')
    },
    changeToPrevPage() {
      this.confirmCode = ''
      this.pinCode = ''
      this.register_step = 1
    },
    async phoneConfirm() {
      const resp = await doRegisterPhoneConfirm(this.user.phone, this.confirmCode)
      if (resp.status === "error") {
        this.errorMessage = resp.message
      } else {
        this.errorMessage = ''
        this.$store.commit('setAuthToken', resp.auth_token)
        this.pinCode = ''
        this.register_step = 3
      }
    },
    onCaptchaTokenReceived(token) {
      this.captchaToken = token
      console.log('Captcha token received in Register:', token)
    },
    onCaptchaTokenExpired() {
      this.captchaToken = ''
      this.errorMessage = 'Время действия капчи истекло. Пожалуйста, пройдите проверку заново.'
    }
  }
}
</script>
